import React, { useEffect, useRef, useState } from "react";
import { Link } from "gatsby";
import Navbar from "./navbar";
import { slug } from "../utils/slug";

const SITE_NAMES = {
  "order.simplysalad.com": "Simply Salad ordering",
  "doordash.com": "DoorDash",
  "ubereats.com": "Uber Eats",
  "grubhub.com": "Grubhub",
  // ✅ Both App Store domains covered — apps.apple.com AND itunes.apple.com
  "apps.apple.com": "Apple App Store",
  "itunes.apple.com": "Apple App Store",
  "play.google.com": "Google Play Store",
  "instagram.com": "Instagram",
  "facebook.com": "Facebook",
  "twitter.com": "Twitter",
  "x.com": "X",
  "yelp.com": "Yelp",
  "bit.ly": "bit dot ly",
  "tinyurl.com": "tinyurl dot com",
};

const normalizeExternalUrl = (url) => {
  if (typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getSiteName = (url) => {
  try {
    const parsedUrl = new URL(normalizeExternalUrl(url));
    // ✅ Strip "www." AND any subdomain like "apps." or "play." for matching
    const hostname = parsedUrl.hostname.replace(/^www\./, "");

    // For URL shorteners, announce the short-link slug (dynamic per link).
    if (hostname === "bit.ly" || hostname === "tinyurl.com") {
      const shortSlug = parsedUrl.pathname
        .replace(/^\/+/, "")
        .split("/")[0]
        ?.replace(/[^a-zA-Z0-9]/g, "")
        ?.toLowerCase();

      if (shortSlug) return shortSlug;
    }

    // ✅ First try exact hostname match (e.g. "apps.apple.com")
    if (SITE_NAMES[hostname]) return SITE_NAMES[hostname];

    // ✅ Then try suffix match (e.g. "something.apple.com" → "apps.apple.com")
    const suffixMatch = Object.entries(SITE_NAMES).find(([domain]) =>
      hostname === domain || hostname.endsWith(`.${domain}`)
    )?.[1];

    if (suffixMatch) return suffixMatch;

    // Fallback to a readable domain label; avoid announcing the raw URL.
    return `the ${hostname} website`;
  } catch {
    return null;
  }
};

const PromoBar = ({ allPromos, isOrderPage }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const timeoutRef = useRef(null);
  const delay = 4000;

  function resetTimeout() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(
      () =>
        setCurrentSlide((prevIndex) =>
          prevIndex === allPromos.length - 1 ? 0 : prevIndex + 1
        ),
      delay
    );
    return () => {
      resetTimeout();
    };
  }, [currentSlide]);

  const promo = allPromos[currentSlide];

const getAriaLabel = (promo) => {
    if (promo.linkType === "external") {
      // ✅ Parse the actual hostname instead of using .includes() —
      // a URL like "apps.apple.com/us/app/simplysalad/id123" contains
      // "simplysalad" in its path but is NOT order.simplysalad.com.
      let isOffSite = true;
      try {
        const hostname = new URL(
          normalizeExternalUrl(promo.link)
        ).hostname.replace(/^www\./, "");
        isOffSite = hostname !== "order.simplysalad.com";
      } catch {
        isOffSite = true;
      }
      if (isOffSite) {
        const siteName = getSiteName(promo.link);
        const isAppStoreLink =
          siteName === "Apple App Store" || siteName === "Google Play Store";
        return siteName
          ? isAppStoreLink
            ? `${promo.title}, redirects to off-site App Store`
            : `${promo.title}, redirects to off-site App Store`
          : `${promo.title}, redirects to off-site App Store`;
      }
      return promo.title;
    }
    const readableSlug = promo.slug.current.replace(/-/g, " ");
    return `${promo.title}, navigates to ${readableSlug}`;
  };

  return (
    <header
      className={`-top-[2px] left-0 w-full z-50 shadow ${
        isOrderPage ? "relative" : "sticky"
      }`}
    >
      {allPromos.length > 0 && (
        <div
          className={`whitespace-nowrap w-full transition-all duration-500 ease-linear h-[51px] text-sm sm:text-base`}
        >
          {promo.linkType === "external" ? (
            <a
              href={promo.link}
              target={
                promo.link?.includes("order.simplysalad.com") ? "_self" : "_blank"
              }
              rel="noopener noreferrer"
              aria-label={getAriaLabel(promo)}
              className="inline-block w-full h-full min-h-[51px] bg-secondary"
            >
              <p
                className="w-full h-full flex items-center justify-center text-white text-center text-wrap"
                // ✅ Hide visible text from AT — the aria-label on <a> is the
                // single source of truth, preventing double-reading.
                aria-hidden="true"
              >
                {promo.title}
              </p>
            </a>
          ) : (
            <Link
              to={slug(promo.slug.current)}
              aria-label={getAriaLabel(promo)}
              className="inline-block w-full h-full min-h-[51px] bg-secondary"
            >
              <p
                className="w-full h-full flex items-center justify-center text-white text-center text-wrap"
                // ✅ Same here — suppress the inner <p> to avoid duplication.
                aria-hidden="true"
              >
                {promo.title}
              </p>
            </Link>
          )}
        </div>
      )}
      <div className="w-full mx-auto bg-white">
        <Navbar />
      </div>
    </header>
  );
};

export default PromoBar;
